import {ShopperProducts} from 'commerce-api'
import {ProductModel} from '../../store/ProductStore'
import {ItemPrices} from '../../types/item-prices'
import {getAppOrigin} from '@salesforce/pwa-kit-react-sdk/utils/url'
import {formatProductName, capitalize, isServer, normalizeRelativeUrl} from '../utils'
import { DateUtils } from '../date-utils'
import {SeasonalHours, StoreDetailResponseItem} from '../../types/store/storeDetails'

function offerValidUntilDate(endDate?: string): string {
  if (endDate) {
    const offerValidDate = new Date(endDate)
    return offerValidDate.toISOString()
  }

  const date = new Date()
  date.setDate(date.getDate() + 30)
  return date.toISOString()
}

export type StructuredListItem = {
  '@type': 'ListItem'
  position: number
  item: {
    '@id': string | undefined
    name: string | undefined
  }
}

export type StructuredBreadcrumbList = {
  '@context': 'https://schema.org'
  '@type': 'BreadcrumbList'
  itemListElement: Array<StructuredListItem>
}

export type StructuredPriceSpecification = {
  '@type': 'PriceSpecification'
  price?: number
  priceCurrency?: string
  priceType?: string
}

type StructuredQuantitativeValue = {
  '@type': 'QuantitativeValue'
  value?: number
  unitText?: string
}

export type StructuredUnitPriceSpecification = {
  '@type': 'UnitPriceSpecification'
  price?: number
  priceCurrency?: string
  priceType?: string
  referenceQuantity?: StructuredQuantitativeValue
}

export type StructuredPrice = StructuredPriceSpecification | StructuredUnitPriceSpecification

enum ItemAvailability {
  InStock = 'https://schema.org/InStock',
  InStoreOnly = 'https://schema.org/InStoreOnly',
  OutOfStock = 'https://schema.org/OutOfStock'
}

export type StructuredOffer = {
  '@type': 'Offer'
  availability: ItemAvailability
  price?: number | string
  priceCurrency?: string
  /** ISO 8601 Date string*/
  priceValidUntil?: string
  priceSpecification?: StructuredPrice | StructuredPrice[]
}

export type Rating = {
  bestRating: string
  ratingValue: string
  worstRating: string
}

export type Review = {
  author: string
  datePublished: string
  reviewBody: string
  name: string
  reviewRating: Rating
}

export type StructuredRating = Rating & {
  '@type': 'Rating'
}

export type StructuredReview = Review & {
  '@type': 'Review'
}

export type StructuredAggregateRating = {
  '@type': 'AggregateRating'
  ratingValue: string
  reviewCount: string
}

export type StructuredProduct = {
  '@context': 'https://schema.org/'
  '@type': 'Product'
  image: string | Record<string, string | Record<string, string>> | undefined
  url: string
  name: string | undefined
  brand?: string
  sku?: string
  gtin13?: string
  offers: StructuredOffer
  aggregateRating?: StructuredAggregateRating
  reviews?: StructuredReview[]
  priceSpecification?: StructuredPrice | StructuredPrice[]
}

type StructuredEntryPoint = {
  '@type': 'EntryPoint'
  urlTemplate: string
}

type StructuredBaseAction = {
  '@type': 'Action'
  name: string
  target: StructuredEntryPoint
}

type StructuredSearchAction = Omit<StructuredBaseAction, '@type'> & {
  '@type': 'SearchAction'
  query?: string
}

type StructuredTradeAction = Omit<StructuredBaseAction, '@type'> & {
  '@type': 'TradeAction'
}

type StructuredFindAction = Omit<StructuredBaseAction, '@type'> & {
  '@type': 'FindAction'
}

type StructuredAction = StructuredBaseAction | StructuredSearchAction | StructuredTradeAction | StructuredFindAction

export type StructuredWebsite = {
  "@context": "https://schema.org"
  "@type": "WebSite"
  "@id": string
  url: string
  name: string
  potentialAction?: Array<StructuredAction>
}

type StructuredWebPage = {
  "@context": "https://schema.org"
  "@type": "WebPage"
  "@id": string
  url: string
  name: string
  description?: string
  isPartOf?: Omit<StructuredWebsite, '@context'>
}

type StructuredContactPoint = {
  "@type": "ContactPoint"
  telephone: string
  contactType: string
  areaServed: string
  availableLanguage: string
}

export type StructuredOnlineStore = {
  "@context": "https://schema.org"
  "@type": "OnlineStore"
  "@id": string
  name: string
  url: string
  logo: string
  sameAs?:  string[]
  contactPoint?: StructuredContactPoint
}

type StructuredItem<ItemT> = {
  '@type': 'ListItem'
  position: number
  item: ItemT
}

export type StructuredItemList<ItemT> = {
  '@context': 'https://schema.org'
  '@type': 'ItemList'
  url: string
  numberOfItems: string
  itemListElement: Array<StructuredItem<ItemT>>
}

export type StructuredCollectionPage = {
  '@context': 'https://schema.org'
  '@type': 'CollectionPage'
  '@id': string
  url: string
  name: string
  description: string
  isPartOf: StructuredWebsite
  mainEntity: StructuredItemList<StructuredProduct>
}

type StructuredPostalAddress = {
  '@type': 'PostalAddress'
  streetAddress?: string
  addressLocality?: string
  addressRegion?: string
  postalCode?: string
  addressCountry?: string
}

type StructuedGeoCoordinates = {
  '@type': 'GeoCoordinates'
  latitude: number
  longitude: number
}

type StructuredOpeningHoursSpecification = {
  '@type': 'OpeningHoursSpecification'
  dayOfWeek: string | string[]
  opens?: string
  closes?: string
  validFrom?: string
  validThrough?: string
  description?: string
}

type StructuredOrganization = {
  '@type': 'Organization'
  '@id': string
}

export type StructuredGroceryStore = {
  '@context': 'https://schema.org'
  '@type': 'GroceryStore'
  '@id': string
  name: string
  url: string
  telephone?: string
  image?: string
  address: StructuredPostalAddress
  geo: StructuedGeoCoordinates
  priceRange: string
  openingHoursSpecification: StructuredOpeningHoursSpecification[]
  specialOpeningHoursSpecification?: StructuredOpeningHoursSpecification[]
  parentOrganization?: StructuredOrganization
}

export type StructuredProductList = StructuredItemList<StructuredProduct>

export type StructuredDataItem =
  | StructuredBreadcrumbList
  | StructuredItemList<StructuredProductList>
  | StructuredProduct
  | StructuredProductList
  | StructuredGroceryStore
  | StructuredWebsite
  | StructuredOnlineStore
  | StructuredWebPage
  | StructuredCollectionPage

type StructuredDataType = Array<StructuredDataItem>

export default StructuredDataType

export const buildListItemStructuredData = (
  listItem: ShopperProducts.PathRecord,
  position: number
): StructuredListItem => ({
  '@type': 'ListItem',
  position: position,
  item: {
    '@id': listItem.id,
    name: listItem.name
  }
})

interface BreadcrumbListItem {
  name?: string
  url?: string
}

export const buildBreadcrumbsStructuredData = (breadcrumbs: BreadcrumbListItem[]): StructuredBreadcrumbList => {
  let itemListElement = breadcrumbs.reduce((prev, curr, index, arr) => {
    // If the last breadcrumb does not have a URL, use the current window location as the URL
    let currentUrl;
    if (index === arr.length - 1 && !curr.url && !isServer) {
      currentUrl = window.location.toString()
    }

    if (!curr.name || (typeof curr.url === 'undefined' && !currentUrl)) {
      return prev
    }

    const relativeUrl = !currentUrl ? normalizeRelativeUrl(curr.url || '') : undefined

    if (!relativeUrl && !currentUrl) {
      return prev
    }

    const url = currentUrl || `${getAppOrigin()}${relativeUrl}`
    
    prev.push({
      '@type': 'ListItem',
      position: index + 1,
      item: {
        '@id': url,
        name: curr.name
      }
    })

    return prev
  }, [] as StructuredListItem[])

  if (!isServer && itemListElement.at(-1)?.item['@id'] && !itemListElement.at(-1)?.item['@id']?.includes(window.location.pathname)) {
    itemListElement.push({
      '@type': 'ListItem',
      position: itemListElement.length + 1,
      item: {
        '@id': window.location.origin + window.location.pathname,
        name: window.document.title.split('|')?.[0]?.trim()
      }
    })
  }

  if (itemListElement.length < 2 && !isServer) {
    itemListElement = [
      {
        '@type': 'ListItem',
        position: 1,
        item: {
          '@id': getAppOrigin(),
          name: 'Home'
        }
      },
      {
        '@type': 'ListItem',
        position: 2,
        item: {
          '@id': window.location.origin + window.location.pathname,
          name: window.document.title.split('|')?.[0]?.trim()
        }
      }
    ]
  }

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement
  }
}

function getItemAvailability(disabledStatus?: string | null): ItemAvailability {
  let availability: ItemAvailability = ItemAvailability.InStock

  if (disabledStatus === 'out-of-stock' || disabledStatus === 'coming-soon') {
    availability = ItemAvailability.OutOfStock
  }

  if (disabledStatus === 'in-store-only') {
    availability = ItemAvailability.InStoreOnly
  }
  return availability
}

export const buildStructuredOfferData = (
  product: ProductModel,
  price: number | null,
  wasPrice: number | null,
): StructuredOffer => {
  const { priceInfo, disabledStatus } = product
  const currency = 'GBP'

  if (price === null) {
    return {
      '@type': 'Offer',
      availability: ItemAvailability.OutOfStock
    }
  }


  let perUnitPrice: number | undefined
  
  if (priceInfo?.[price]) {
    perUnitPrice = Number(priceInfo?.[price]?.replace('£', ''))
  }

  const offers: StructuredOffer = {
    '@type': 'Offer',
    availability: getItemAvailability(disabledStatus),
    price,
    priceCurrency: currency,
    priceValidUntil: offerValidUntilDate(),
    priceSpecification: [
      {
        '@type': 'PriceSpecification',
        price,
        priceCurrency: currency
      }
    ]
  }

  if (wasPrice && wasPrice !== price && Array.isArray(offers.priceSpecification)) {
    offers.priceSpecification.push(
      {
        '@type': 'PriceSpecification',
        priceType: 'https://schema.org/StrikethroughPrice',
        price: wasPrice,
        priceCurrency: currency
      })
  }

  const { unitAmount, unitText } = priceInfo?.perUnit?.match(/(?<unitAmount>\d+)(?<unitText>\w+)/)?.groups ?? {}

  if (perUnitPrice && unitAmount && unitText && Array.isArray(offers.priceSpecification)) {
    offers.priceSpecification.push(
      {
        '@type': 'UnitPriceSpecification',
        price: perUnitPrice,
        priceCurrency: currency,
        referenceQuantity: {
          '@type': 'QuantitativeValue',
          value: Number(unitAmount),
          unitText: unitText
        }
      }
    )
  }

  return offers
}

export const buildStructuredAggregateRatingData = (
  productRating: number,
  productReviewCount: number
): StructuredAggregateRating => {
  return {
    '@type': 'AggregateRating',
    ratingValue: productRating?.toString(),
    reviewCount: productReviewCount?.toString()
  }
}

export const buildStructuredRatingData = ({
  bestRating,
  ratingValue,
  worstRating
}: Rating): StructuredRating => ({
  '@type': 'Rating',
  bestRating,
  ratingValue,
  worstRating
})

export const buildStructuredReviewData = ({
  author,
  datePublished,
  reviewBody,
  name,
  reviewRating
}: Review): StructuredReview => ({
  '@type': 'Review',
  author,
  datePublished,
  reviewBody,
  name,
  reviewRating: buildStructuredRatingData(reviewRating)
})

export const buildProductModelStructured = (
  product: ProductModel,
  priceForData: number | null,
  wasPriceforData: number | null
): StructuredProduct => {
  const {image_groups, name, id, brand, imageGroups, productRating, productReviewCount } = product
  const url = `${getAppOrigin()}/p/${formatProductName(name)}/${id}.html`
  const image = image_groups?.[0]?.images?.[0] || imageGroups?.[0]?.images?.[0]

  return {
    '@context': 'https://schema.org/',
    '@type': 'Product',
    image: {
      '@type': 'ImageObject',
      contentUrl: image?.dis_base_link || image?.disBaseLink,
      license: 'https://iceland.co.uk',
      acquireLicensePage: 'https://iceland.co.uk',
      creditText: 'Iceland',
      creator: {
        '@type': 'Organization',
        name: 'Iceland'
      },
      copyrightNotice: 'Iceland'
    },
    url,
    brand,
    name,
    sku: id,
    offers: buildStructuredOfferData(product, priceForData, wasPriceforData),
    aggregateRating: buildStructuredAggregateRatingData(productRating, productReviewCount)
  }
}

export const buildProductListStructuredData = (
  products: ItemPrices[],
  pathname = ''
): StructuredProductList => {
  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    url: getAppOrigin() + pathname,
    numberOfItems: products.length.toString(),
    itemListElement: products.map(({hit, lowestPrice, lowestWasPrice}, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      item: buildProductModelStructured(hit, lowestPrice ?? null, lowestWasPrice ?? null)
    }))
  }
}

export const buildStructuredGroceryStoreData = (
  store: StoreDetailResponseItem,
  seasonalHours: SeasonalHours[] | null
): StructuredGroceryStore => {
  const appOrigin = getAppOrigin()

  return {
    '@context': 'https://schema.org',
    '@type': 'GroceryStore',
    '@id': appOrigin,
    name: `Iceland Foods (${store.name})`,
    url: `${appOrigin}/store-finder/store?StoreID=${store.id}`,
    telephone: store.phone.replace(/^0/, '+44'),
    priceRange: '££',
    address: {
      '@type': 'PostalAddress',
      streetAddress: [store.address1, store.address2].filter(Boolean).join(', '),
      addressLocality: store.city,
      postalCode: store.postalCode,
      addressCountry: 'GB'
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: store.latitude,
      longitude: store.longitude
    },
    openingHoursSpecification: store.c_storeHoursJson?.reduce((prev, curr) => {
      if (curr.isClosed) {
        prev.push({
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: capitalize(curr.day) || curr.day
        })

        return prev
      }

      prev.push({
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: capitalize(curr.day) || curr.day,
        opens: DateUtils.convert12HourTo24Hour(curr.open),
        closes: DateUtils.convert12HourTo24Hour(curr.close)
      })

      return prev
    }, [] as StructuredOpeningHoursSpecification[]),
    specialOpeningHoursSpecification: seasonalHours?.reduce((prev, curr) => {
      if (curr.isClosed && curr.validFrom && curr.validTo) {
        prev.push({
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: curr.day,
          validFrom: curr.validFrom,
          validThrough: curr.validTo
        })
        return prev
      }

      if (curr.isClosed || !curr.open24HourTime || !curr.close24HourTime) {
        return prev
      }

      prev.push({
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: curr.day,
        opens: curr.open24HourTime,
        closes: curr.close24HourTime,
        validFrom: curr.validFrom,
        validThrough: curr.validTo
      })

      return prev
    }, [] as StructuredOpeningHoursSpecification[]),
    parentOrganization: {
      '@type': 'Organization',
      '@id': appOrigin
    }
  }
}

export const buildHomepageStructuredData = (): StructuredOnlineStore => {
  const appOrigin = getAppOrigin()

  return {
    '@context': 'https://schema.org',
    '@type': 'OnlineStore',
    '@id': `${appOrigin}/#OnlineStore`,
    name: 'Iceland Foods',
    url: appOrigin,
    logo: `${appOrigin}/logo.svg`,
    sameAs: [
      'https://www.facebook.com/IcelandFoods',
    ],
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '+44-800-328-0800',
      contactType: 'customer service',
      areaServed: 'GB',
      availableLanguage: 'English',
    }
  }
}

export const buildWebsiteStructuredData = (): StructuredWebsite => {
  const appOrigin = getAppOrigin()

  return {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  '@id': `${appOrigin}/#WebSite`,
  url: appOrigin,
  name: 'Iceland Foods',
  potentialAction: [
        {
          '@type': 'SearchAction',
          name: 'Search for groceries online',
          target: {
            '@type': 'EntryPoint',
            urlTemplate: `${appOrigin}/search?q={search_term_string}`,
          },
          query: 'required name=search_term_string',
        },
        {
          '@type': 'TradeAction',
          name: 'Order groceries online',
          target: {
            '@type': 'EntryPoint',
            urlTemplate: appOrigin,
          },
        },
        {
          '@type': 'FindAction',
          name: 'Find an Iceland store near you',
          target: {
            '@type': 'EntryPoint',
            urlTemplate: `${appOrigin}/store-finder`,
          },
        },
      ]
  }
}

export const buildCollectionPageStructuredData = (info: Pick<StructuredCollectionPage, 'name' | 'description'>, pathName: string, products: ItemPrices[]): StructuredCollectionPage => {
  const websiteStructuredData = buildWebsiteStructuredData()
  
  const url = `${getAppOrigin()}${pathName}`

  return {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    '@id': `${url}/#CollectionPage`,
    url: url,
    name: info.name,
    description: info.description,
    isPartOf: { ...websiteStructuredData, potentialAction: undefined },
    mainEntity: buildProductListStructuredData(products, pathName)
  }
}

export const buildeWebPageStructuredData = (name: string, path: `/${string}`, description: string): StructuredWebPage => {
  const appOrigin = getAppOrigin()

  const websiteStructuredData = { ...buildWebsiteStructuredData(), '@context': undefined }

  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    '@id': `${appOrigin}${path}/#webpage`,
    url: `${appOrigin}${path}`,
    name,
    description,
    isPartOf: websiteStructuredData
  }
}